import React from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalBody, ModalFooter, ModalHeader, Button } from 'reactstrap';
import { useTranslation } from 'react-i18next';

/**
 * Confirmation modal shown before any active-context switch — both org-to-org
 * and switching to/from Global View. Asks the user to confirm so accidental
 * clicks in the topbar switcher (or the Global Dashboard's "jump into org"
 * buttons) don't drop them out of the page they're currently working on.
 *
 * <p>Pass {@code targetName} so the confirmation message can name the target
 * context. {@code switching} disables the action button while the underlying
 * switch-context request is in flight.
 */
const SwitchContextConfirmModal = ({
  isOpen,
  targetName,
  switching = false,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  return (
    <Modal isOpen={isOpen} toggle={onCancel} centered>
      <ModalHeader toggle={onCancel}>
        {t('shared.org_switcher.confirm_title', 'Switch organization?')}
      </ModalHeader>
      <ModalBody>
        {t(
          'shared.org_switcher.confirm_message',
          "You're about to switch to {{name}}. Any unsaved work in the current organization may be lost.",
          { name: targetName || '' }
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onCancel} disabled={switching}>
          {t('shared.cancel', 'Cancel')}
        </Button>
        <Button color="primary" onClick={onConfirm} disabled={switching}>
          {switching
            ? t('shared.global_org.switching_context', 'Switching context…')
            : t('shared.org_switcher.confirm_button', 'Switch')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

SwitchContextConfirmModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  targetName: PropTypes.string,
  switching: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default SwitchContextConfirmModal;
